import { Package, Boxes, ShoppingBag, Layers, ExternalLink } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '@components/ui/popover';
import { Calendar } from '@components/ui/calendar';
import { Button } from '@components/ui/button';
import { NativeSelect, NativeSelectOption } from '@components/ui/native-select';
import { Label } from '@components/ui/label';
import { CalendarIcon } from 'lucide-react';
import { SectionBadge } from './SectionBadge';
import { OptionCard } from './OptionCard';
import { FormatOptionCard } from './FormatOptionCard';
import { useRovingRadioGroup } from '@generated/hooks/useRovingRadioGroup';
import type { Commitment, FormatItem, Frequency, GlobalConfig, Transportation, Unit } from '@generated/types';
import { buildBoxCeilingNote, buildSizingLine, isValidHttpUrl, palletNote, transportationHelperText } from '@generated/utils/pricing';

const TRANSPORT_OPTIONS: { value: Transportation; label: string; description: string }[] = [
  {
    value: 'Partner arranges',
    label: 'Customer-Arranged Transportation',
    description: "We'll arrange and cover transportation to ByFusion",
  },
  {
    value: 'ByFusion arranges',
    label: 'ByFusion to arrange transportation',
    description: 'Have ByFusion help arrange pickup and freight',
  },
];

const SKELETON_FORMAT_IDS = ['sk-fmt-1', 'sk-fmt-2', 'sk-fmt-3', 'sk-fmt-4'];

const COMMITMENT_OPTIONS: { value: Commitment; label: string }[] = [
  { value: 'Month-to-Month', label: 'Month-to-month' },
  { value: '6 months', label: '6 months' },
  { value: '12 months', label: '12 months' },
  { value: '24 months', label: '24 months' },
  { value: '36 months', label: '36 months' },
  { value: '48 months', label: '48 months' },
];

function formatIcon(name: string) {
  const n = name.toLowerCase();
  if (n.includes('box')) return Package;
  if (n.includes('gaylord')) return Boxes;
  if (n.includes('bag')) return ShoppingBag;
  if (n.includes('bale')) return Layers;
  return Package;
}

function specText(f: FormatItem): string {
  return f.palletRequired ? 'Shipped on pallets.' : 'Round-trip shipping and pickup included.';
}

function rangeText(f: FormatItem): string {
  const fill = f.nominalFillLbs ?? 0;
  return f.volumetric ? `Up to ${fill.toLocaleString()} lbs on average` : `About ${fill.toLocaleString()} lbs per unit`;
}

interface LogisticsCardProps {
  formats: FormatItem[];
  loading: boolean;
  formatId: string | null;
  onFormatChange: (id: string) => void;
  disabledFormatIds: Set<string>;
  boxCeilingGlobal: GlobalConfig | null;
  collectionBoxFormat: FormatItem | undefined;
  frequency: Frequency | null;
  transportation: Transportation | null;
  onTransportationChange: (t: Transportation) => void;
  desiredShipmentDate: Date | undefined;
  onDateChange: (d: Date | undefined) => void;
  commitment: Commitment | null;
  onCommitmentChange: (c: Commitment) => void;
  validCommitments: Commitment[];
  monthlyWeightLbs: number;
  unit: Unit;
  unitsPerShipment: number;
  fillWeightLbs: number;
  showErrors: boolean;
  desiredShipmentDateInvalid: boolean;
}

export function LogisticsCard(props: LogisticsCardProps) {
  const {
    formats,
    loading,
    formatId,
    onFormatChange,
    disabledFormatIds,
    boxCeilingGlobal,
    collectionBoxFormat,
    frequency,
    transportation,
    onTransportationChange,
    desiredShipmentDate,
    onDateChange,
    commitment,
    onCommitmentChange,
    validCommitments,
    monthlyWeightLbs,
    unit,
    unitsPerShipment,
    fillWeightLbs,
    showErrors,
    desiredShipmentDateInvalid,
  } = props;

  const selectedFormat = formats.find((f) => f.id === formatId);
  const sizingLine = frequency
    ? buildSizingLine(monthlyWeightLbs, unit, frequency, selectedFormat, unitsPerShipment, fillWeightLbs)
    : "Enter your monthly weights above and we'll size the shipment for you.";

  const boxOverCeiling = disabledFormatIds.has(collectionBoxFormat?.id ?? '');
  const boxCeilingNote = boxOverCeiling ? buildBoxCeilingNote(boxCeilingGlobal, collectionBoxFormat) : null;

  const isCollectionBoxes = selectedFormat?.name === 'Collection Boxes';
  const buyUrl = isCollectionBoxes ? selectedFormat?.buyUrl?.url : null;
  const showBuyCta = isCollectionBoxes && isValidHttpUrl(buyUrl);

  const pallet = palletNote(selectedFormat);
  const transportHelper = transportationHelperText(selectedFormat, transportation);

  const formatValues = formats.map((f) => f.id);
  const { getItemProps: getFormatItemProps } = useRovingRadioGroup(formatValues, formatId, onFormatChange, disabledFormatIds);

  const transportValues = TRANSPORT_OPTIONS.map((o) => o.value);
  const { getItemProps: getTransportItemProps } = useRovingRadioGroup(transportValues, transportation, onTransportationChange);

  return (
    <section className="mt-5 rounded-xl border border-border bg-card p-6 shadow-[var(--shadow-stack)] sm:p-8">
      <div className="flex items-start gap-3">
        <SectionBadge number={3} />
        <div>
          <h2 className="font-[family-name:var(--font-heading)] text-xl font-medium text-foreground">
            Your shipping method and transportation coordination preference
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Collection Boxes are the fastest way to get started. Gaylords, bulk bags, and bales must be palletized
            for shipment. If you expect to use more than one shipping method, select the largest method you plan to
            use. We&rsquo;ll confirm the final logistics in your agreement.
          </p>
        </div>
      </div>

      <div role="radiogroup" aria-label="Shipping method" className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {loading
          ? SKELETON_FORMAT_IDS.map((skeletonId) => (
              <div key={skeletonId} className="h-28 animate-pulse rounded-lg bg-secondary" />
            ))
          : formats.map((f) => (
              <FormatOptionCard
                key={f.id}
                icon={formatIcon(f.name)}
                name={f.name}
                spec={specText(f)}
                range={rangeText(f)}
                selected={f.id === formatId}
                disabled={disabledFormatIds.has(f.id)}
                onSelect={() => onFormatChange(f.id)}
                groupProps={getFormatItemProps(f.id)}
              />
            ))}
      </div>

      {boxCeilingNote ? (
        <p className="mt-3 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-xs text-foreground">
          {boxCeilingNote}
        </p>
      ) : null}

      <p className="tnum mt-3 rounded-md bg-secondary px-3 py-2 text-xs text-muted-foreground">{sizingLine}</p>

      {showBuyCta ? (
        <div className="mt-3">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="border-primary text-primary hover:bg-primary/5"
            onClick={() => window.open(buyUrl as string, '_blank', 'noopener,noreferrer')}
          >
            Start today
            <ExternalLink className="h-3.5 w-3.5" />
          </Button>
          <p className="mt-1.5 text-xs text-muted-foreground">
            No need to wait — subscribe and start filling boxes now.
          </p>
        </div>
      ) : null}

      {pallet ? <p className="mt-3 text-xs text-muted-foreground">{pallet}</p> : null}

      <div className="mt-6">
        <Label id="transportation-label" className="text-xs font-medium text-foreground">
          Transportation Coordination
        </Label>
        <div
          role="radiogroup"
          aria-labelledby="transportation-label"
          className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2"
        >
          {TRANSPORT_OPTIONS.map((opt) => (
            <OptionCard
              key={opt.value}
              title={opt.label}
              description={opt.description}
              selected={transportation === opt.value}
              onSelect={() => onTransportationChange(opt.value)}
              groupProps={getTransportItemProps(opt.value)}
            />
          ))}
        </div>
        {transportHelper ? <p className="mt-1.5 text-xs text-muted-foreground">{transportHelper}</p> : null}
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <Label htmlFor="desired-shipment-date" className="text-xs font-medium text-foreground">
            Target first shipment date
          </Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                id="desired-shipment-date"
                variant="outline"
                aria-invalid={showErrors && desiredShipmentDateInvalid}
                className="mt-1.5 w-full justify-start text-left font-normal"
              >
                <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                {desiredShipmentDate ? desiredShipmentDate.toLocaleDateString('en-US') : 'Select a date'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar mode="single" selected={desiredShipmentDate} onSelect={onDateChange} />
            </PopoverContent>
          </Popover>
        </div>
        <div>
          <Label htmlFor="commitment-length" className="text-xs font-medium text-foreground">
            Commitment length
          </Label>
          <NativeSelect
            id="commitment-length"
            className="mt-1.5"
            value={commitment ?? ''}
            onChange={(e) => onCommitmentChange(e.target.value as Commitment)}
          >
            <NativeSelectOption value="" disabled>
              Select commitment length
            </NativeSelectOption>
            {COMMITMENT_OPTIONS.map((opt) => (
              <NativeSelectOption key={opt.value} value={opt.value} disabled={!validCommitments.includes(opt.value)}>
                {opt.label}
              </NativeSelectOption>
            ))}
          </NativeSelect>
          {commitment == null ? (
            <p className="mt-1.5 text-xs text-muted-foreground">Choose a commitment length that fits this frequency.</p>
          ) : null}
        </div>
      </div>
    </section>
  );
}
